import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for loading images without external dependencies
 * Replaces the use-image library with native JavaScript Image API
 * 
 * @param {string} src - Image source URL or data URL
 * @param {Object} options - Loading options
 * @returns {Object} - { image, loading, error }
 */
export const useNativeImage = (src, options = {}) => {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const imageRef = useRef(null);
  const { crossOrigin = null, referrerPolicy = null } = options;
  
  useEffect(() => {
    if (!src) {
      setImage(null);
      setLoading(false);
      setError(null);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Create new image instance
    const img = new Image();
    imageRef.current = img;
    
    // Set up event handlers
    const handleLoad = () => {
      if (imageRef.current === img) {
        setImage(img);
        setLoading(false);
        setError(null);
      }
    };
    
    const handleError = (e) => {
      if (imageRef.current === img) {
        const errorMessage = `Failed to load image: ${src}`;
        console.warn(errorMessage, e);
        setError(errorMessage);
        setLoading(false);
        setImage(null);
      }
    };
    
    // Attach event listeners
    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    
    // Set optional attributes
    if (crossOrigin) {
      img.crossOrigin = crossOrigin;
    }
    if (referrerPolicy) {
      img.referrerPolicy = referrerPolicy;
    }
    
    // Start loading
    img.src = src;
    
    // Cleanup function
    return () => {
      if (imageRef.current === img) {
        img.removeEventListener('load', handleLoad);
        img.removeEventListener('error', handleError);
        imageRef.current = null;
      }
    };
  }, [src, crossOrigin, referrerPolicy]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (imageRef.current) {
        imageRef.current.src = '';
        imageRef.current = null;
      }
    };
  }, []);
  
  return { image, loading, error };
};

/**
 * Hook for preloading multiple images
 * Useful for loading image sets or sprites
 * 
 * @param {string[]} sources - Array of image source URLs
 * @returns {Object} - { images, loading, errors, progress }
 */
export const useNativeImages = (sources = []) => {
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [progress, setProgress] = useState(0);
  
  const imageRefs = useRef({});
  
  useEffect(() => {
    if (!sources.length) {
      setImages({});
      setLoading(false);
      setErrors({});
      setProgress(0);
      return;
    }
    
    setLoading(true);
    setErrors({});
    setProgress(0);
    
    let loadedCount = 0;
    const totalCount = sources.length;
    const newImages = {};
    const newErrors = {};
    
    const updateProgress = () => {
      const currentProgress = (loadedCount / totalCount) * 100;
      setProgress(currentProgress);
      
      if (loadedCount === totalCount) {
        setImages(newImages);
        setErrors(newErrors);
        setLoading(false);
      }
    };
    
    sources.forEach((src, index) => {
      const img = new Image();
      imageRefs.current[src] = img;
      
      img.onload = () => {
        if (imageRefs.current[src] === img) {
          newImages[src] = img;
          loadedCount++;
          updateProgress();
        }
      };
      
      img.onerror = (e) => {
        if (imageRefs.current[src] === img) {
          newErrors[src] = `Failed to load image: ${src}`;
          loadedCount++;
          updateProgress();
        }
      };
      
      img.src = src;
    });
    
    // Cleanup function
    return () => {
      Object.values(imageRefs.current).forEach(img => {
        if (img) {
          img.onload = null;
          img.onerror = null;
          img.src = '';
        }
      });
      imageRefs.current = {};
    };
  }, [sources]);
  
  return { images, loading, errors, progress };
};

/**
 * Hook for creating image from canvas or other sources
 * Useful for generating thumbnails or processing images
 * 
 * @param {HTMLCanvasElement|string} source - Canvas element or data URL
 * @param {string} format - Image format (default: 'image/png')
 * @param {number} quality - Image quality for JPEG (0-1)
 * @returns {Object} - { image, dataUrl, loading, error }
 */
export const useImageFromCanvas = (source, format = 'image/png', quality = 1.0) => {
  const [image, setImage] = useState(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!source) {
      setImage(null);
      setDataUrl(null);
      setLoading(false);
      setError(null);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let url;
      
      if (source instanceof HTMLCanvasElement) {
        url = source.toDataURL(format, quality);
      } else if (typeof source === 'string') {
        url = source;
      } else {
        throw new Error('Invalid source type. Expected HTMLCanvasElement or string.');
      }
      
      setDataUrl(url);
      
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setLoading(false);
      };
      
      img.onerror = () => {
        setError('Failed to create image from source');
        setLoading(false);
      };
      
      img.src = url;
      
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [source, format, quality]);
  
  return { image, dataUrl, loading, error };
};

export default useNativeImage; 